import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProducts,
  setLastFetchedPage,
  setPage,
  setSearch,
} from "../../redux/slices/productSlice";
import { RootState } from "../../redux/store";
import { useSearchParams } from "react-router-dom";
import { SearchHeader } from "../../components/admin/Common/SearchHeader";
import { ProductPanelItem } from "../../components/admin/ProductsPanel/ProductsPanelItem";
import { ProductDetailsOverlay } from "../../components/admin/ProductsPanel/ProductDetailsOverlay";
import { Product } from "../../types/product.types";
import { Plus } from "lucide-react";
import { AddProductOverlay } from "../../components/admin/ProductsPanel/AddProductOverlay";

export const ProductsPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [params, setParams] = useSearchParams();
  const urlSearch = params.get("search") || "";

  const [selectedProductID, setSelectedProductID] = useState<string>("");
  const [showAddProductOverlay, setShowAddProductOverlay] =
    useState<boolean>(false);

  const { items, page, loading, hasMore, search, lastFetechedPage } =
    useSelector((state: RootState) => state.products);

  useEffect(() => {
    if (urlSearch !== search) {
      dispatch(setSearch(urlSearch));
      dispatch(setPage(1));
    }
  }, [urlSearch, dispatch]);

  useEffect(() => {
    if (page <= lastFetechedPage) return;

    dispatch(fetchProducts({ page, search: search || urlSearch }) as any);
    dispatch(setLastFetchedPage(page));
  }, [page, urlSearch, lastFetechedPage, dispatch, search]);

  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        dispatch(setPage(page + 1));
      }
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore, page, dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    dispatch(setSearch(value));
    dispatch(setPage(1));

    if (!value.trim()) {
      params.delete("search");
      setParams(params, { replace: true });
    } else {
      setParams({ search: value }, { replace: true });
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t("Products")}</h1>

        <button
          onClick={() => setShowAddProductOverlay(true)}
          title={t("Add Product")}
          className="h-10 w-10 flex items-center justify-center rounded-lg shadow bg-popupLight dark:bg-popupDark hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <Plus size={18} />
        </button>
      </div>

      <SearchHeader
        search={search}
        handleSearchChange={handleSearchChange}
        searchBarPlaceHolder="Enter product name, product id or description"
      />

      {items.length > 0 && (
        <div className="mt-6 space-y-3">
          {items.map((product) => (
            <ProductPanelItem
              product={product}
              setSelectedProductID={setSelectedProductID}
            />
          ))}
        </div>
      )}

      {showAddProductOverlay && (
        <AddProductOverlay onClose={() => setShowAddProductOverlay(false)} />
      )}

      {!loading && items.length === 0 && (
        <div className="mt-10 text-center text-gray-500 dark:text-gray-400">
          {t("noProductsFound") || "No products found."}
        </div>
      )}

      {loading && (
        <div className="mt-6 text-center text-sm text-gray-500">
          {t("Loading...")}
        </div>
      )}

      {selectedProductID && (
        <ProductDetailsOverlay
          product={items.find((p) => p?._id === selectedProductID) as Product}
          onClose={() => setSelectedProductID("")}
        />
      )}

      <div ref={loadMoreRef} className="h-6" />
    </div>
  );
};
